import React, { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../contexts/AuthContext";
import {
  updateProfile,
  getLawyerProfile,
  updateLawyerProfile,
  getUserSubscription,
  cancelSubscription,
  reactivateSubscription,
  getUserUsageSummary,
  getUserCredits,
  getCreditTransactions,
  createPortalSession,
  deleteUserAccount,
} from "@/shared/db";
import { formatMegabytes } from "@/shared/core/utils/formatBytes";
import { usePlanLimits } from "../hooks/usePlanLimits";
import { BuyCreditsModal } from "../features/payments/components/BuyCreditsModal";
import { Button, Input, Select } from "@/shared/ui";
import { PricingModal } from "../features/payments/components/PricingModal";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import { useConfirm } from "../hooks/useConfirm";
import { toast } from "sonner";
import { analytics } from "../utils/analytics";
import { ROUTES } from "@/shared/config";
import { useNavigate } from "react-router-dom";
import {
  UserIcon,
  ShieldCheckIcon,
  BellIcon,
  GlobeAltIcon,
  EyeIcon,
  TrashIcon,
  ChartBarIcon,
  DocumentTextIcon,
  SparklesIcon,
  CheckCircleIcon,
  ArrowUpIcon,
  CreditCardIcon,
  ChatBubbleLeftRightIcon,
  BriefcaseIcon,
  MagnifyingGlassIcon,
  DocumentMagnifyingGlassIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";
import { motion } from "framer-motion";

type SettingsTab =
  | "profile"
  | "security"
  | "notifications"
  | "preferences"
  | "privacy"
  | "subscription"
  | "usage"
  | "account";

export const SettingsPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { user, profile, refreshProfile, resetPassword, signOut } = useAuth();
  const { confirm, Modal } = useConfirm();
  const navigate = useNavigate();
  const {
    limits,
    loading: limitsLoading,
    refresh,
    subscription,
  } = usePlanLimits();

  const [activeTab, setActiveTab] = useState<SettingsTab>("profile");
  const [saving, setSaving] = useState(false);
  const [subscriptionData, setSubscriptionData] = useState<any>(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(true);
  const [usageSummary, setUsageSummary] = useState<Record<string, number>>({});
  const [canceling, setCanceling] = useState(false);
  const [pricingModalOpen, setPricingModalOpen] = useState(false);
  const [buyCreditsModalOpen, setBuyCreditsModalOpen] = useState(false);
  const [openingPortal, setOpeningPortal] = useState(false);
  const [creditsData, setCreditsData] = useState<{
    balance: number;
    transactions: any[];
  } | null>(null);

  // Profile settings
  const [profileData, setProfileData] = useState({
    full_name: "",
    email: "",
  });

  // Notification settings - Dynamic based on user type
  const getInitialNotifications = (_userType: string) => {
    const baseNotifications = {
      emailNotifications: true,
      documentAlerts: true,
      marketingEmails: false,
    };

    // Lawyers, clients, and firms have consultations and cases
    return {
      ...baseNotifications,
      caseUpdates: true,
      consultationRequests: true,
    };
  };

  const [notifications, setNotifications] = useState(
    getInitialNotifications(profile?.user_type || "client"),
  );

  // Preferences
  const [preferences, setPreferences] = useState({
    language: "es",
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  });

  // Privacy settings
  const [privacy, setPrivacy] = useState({
    profileVisibility: "public",
    showEmail: false,
    showPhone: false,
    allowSearch: true,
  });

  const isTrialing =
    subscriptionData?.status === "trialing" ||
    (subscriptionData as { plan_type?: string })?.plan_type ===
      "lawyer_trial" ||
    subscription?.status === "trialing" ||
    (subscription?.planType as string) === "lawyer_trial";

  const formatPlanLabel = (
    planType?: string | null,
    fallbackTier?: string | null,
  ) => {
    if (planType) {
      return planType
        .replace(/^lawyer_/, "")
        .replace(/_/g, " ")
        .replace(/\b\w/g, (char: string) => char.toUpperCase());
    }
    if (fallbackTier) {
      return fallbackTier
        .replace(/_/g, " ")
        .replace(/\b\w/g, (char: string) => char.toUpperCase());
    }
    return "Free";
  };

  useEffect(() => {
    if (profile) {
      setProfileData({
        full_name: profile.full_name || "",
        email: profile.email || "",
      });
      const defaults = getInitialNotifications(profile.user_type);
      const fromProfile = (
        profile as { notification_preferences?: Record<string, boolean> }
      ).notification_preferences;
      const fromStorage = localStorage.getItem("notifications");
      if (fromProfile && typeof fromProfile === "object") {
        setNotifications({ ...defaults, ...fromProfile });
      } else if (fromStorage) {
        try {
          setNotifications({ ...defaults, ...JSON.parse(fromStorage) });
        } catch {
          setNotifications(defaults);
        }
      } else {
        setNotifications(defaults);
      }
    }

    const savedLanguage = localStorage.getItem("language") || "es";
    setPreferences((p) => ({
      ...p,
      language: savedLanguage,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    }));

    if (profile?.user_type === "lawyer" || profile?.user_type === "firm") {
      getLawyerProfile(profile.id)
        .then((lp) => {
          const lpAny = lp as {
            profile_visibility?: string;
            show_email?: boolean;
            show_phone?: boolean;
            allow_search?: boolean;
          };
          if (lpAny) {
            setPrivacy({
              profileVisibility: lpAny.profile_visibility || "public",
              showEmail: lpAny.show_email ?? false,
              showPhone: lpAny.show_phone ?? false,
              allowSearch: lpAny.allow_search ?? true,
            });
          }
        })
        .catch(() => {
          const savedPrivacy = localStorage.getItem("privacy");
          if (savedPrivacy) {
            try {
              setPrivacy(JSON.parse(savedPrivacy));
            } catch (e) {
              console.error("Error loading privacy:", e);
            }
          }
        });
    } else {
      const savedPrivacy = localStorage.getItem("privacy");
      if (savedPrivacy) {
        try {
          setPrivacy(JSON.parse(savedPrivacy));
        } catch (e) {
          console.error("Error loading privacy:", e);
        }
      }
    }
  }, [profile]);

  // Cache for subscription data to avoid reloading on tab changes
  const subscriptionDataCacheRef = useRef<{
    data: any;
    usageSummary: Record<string, number>;
    creditsData: { balance: number; transactions: any[] };
    timestamp: number;
  } | null>(null);
  const loadingSubscriptionRef = useRef(false);
  const CACHE_DURATION = 30000; // 30 seconds

  // Load usage data is now handled by loadSubscriptionData which loads usageSummary

  const loadSubscriptionData = useCallback(
    async (force = false) => {
      if (!user) return;

      // Check cache first
      const cached = subscriptionDataCacheRef.current;
      const now = Date.now();

      if (!force && cached && now - cached.timestamp < CACHE_DURATION) {
        setSubscriptionData(cached.data);
        setUsageSummary(cached.usageSummary);
        setCreditsData(cached.creditsData);
        setSubscriptionLoading(false);
        return;
      }

      // Prevent concurrent loads
      if (loadingSubscriptionRef.current) {
        return;
      }

      try {
        loadingSubscriptionRef.current = true;
        setSubscriptionLoading(true);
        const sub = await getUserSubscription(user.id);
        setSubscriptionData(sub);

        // Load usage summary
        const summary = await getUserUsageSummary(user.id);
        setUsageSummary(summary);

        // Load credits data
        let creditsDataValue: { balance: number; transactions: any[] } = {
          balance: 0,
          transactions: [],
        };
        try {
          const [credits, transactions] = await Promise.all([
            getUserCredits(user.id),
            getCreditTransactions(user.id, 20),
          ]);
          creditsDataValue = {
            balance: credits?.credits_balance || 0,
            transactions: transactions || [],
          };
          setCreditsData(creditsDataValue);
        } catch (creditsError) {
          console.error("Error loading credits:", creditsError);
          setCreditsData(creditsDataValue);
        }

        // Update cache
        subscriptionDataCacheRef.current = {
          data: sub,
          usageSummary: summary,
          creditsData: creditsDataValue,
          timestamp: now,
        };
      } catch (error) {
        console.error("Error loading subscription data:", error);
      } finally {
        loadingSubscriptionRef.current = false;
        setSubscriptionLoading(false);
      }
    },
    [user],
  );

  // Load data only when user changes
  useEffect(() => {
    if (!user) return;

    // Load subscription data on mount (cached)
    loadSubscriptionData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]); // Only depend on user.id, not the entire loadSubscriptionData function

  // Refresh subscription data when needed (e.g., after canceling subscription)
  const refreshSubscriptionData = useCallback(() => {
    subscriptionDataCacheRef.current = null; // Clear cache
    loadSubscriptionData(true);
  }, [loadSubscriptionData]);

  const handleSaveProfile = async () => {
    if (!user) return;

    try {
      setSaving(true);
      await updateProfile(user.id, {
        full_name: profileData.full_name,
      });
      await refreshProfile();
      toast.success(t("settings.profile.saved"));
    } catch (error) {
      console.error("Error saving profile:", error);
      toast.error(t("settings.profile.error"));
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    try {
      setSaving(true);
      await resetPassword(profileData.email);
      toast.success(t("settings.security.resetEmailSent"));
    } catch (error) {
      console.error("Error changing password:", error);
      toast.error(t("settings.security.error"));
    } finally {
      setSaving(false);
    }
  };

  const handleSaveNotifications = async () => {
    try {
      setSaving(true);
      if (user) {
        await updateProfile(user.id, {
          notification_preferences: notifications as Record<string, boolean>,
        });
        refreshProfile?.();
      }
      localStorage.setItem("notifications", JSON.stringify(notifications));
      toast.success(t("settings.notifications.saved"));
    } catch (e) {
      console.error("Error saving notifications:", e);
      toast.error(t("settings.error"));
    } finally {
      setSaving(false);
    }
  };

  const handleSavePreferences = async () => {
    try {
      setSaving(true);
      localStorage.setItem("language", preferences.language);
      i18n.changeLanguage(preferences.language);
      toast.success(t("settings.preferences.saved"));
    } catch (e) {
      console.error("Error saving preferences:", e);
      toast.error(t("settings.error"));
    } finally {
      setSaving(false);
    }
  };

  const handleSavePrivacy = async () => {
    try {
      setSaving(true);
      const isLawyer =
        profile?.user_type === "lawyer" || profile?.user_type === "firm";
      if (user && isLawyer) {
        await updateLawyerProfile(user.id, {
          profile_visibility: privacy.profileVisibility,
          show_email: privacy.showEmail,
          show_phone: privacy.showPhone,
          allow_search: privacy.allowSearch,
        });
      }
      localStorage.setItem("privacy", JSON.stringify(privacy));
      toast.success(t("settings.privacy.saved"));
    } catch (e) {
      console.error("Error saving privacy:", e);
      toast.error(t("settings.error"));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAccount = async () => {
    await confirm({
      title: t("settings.account.deleteTitle"),
      message: t("settings.account.deleteConfirm"),
      confirmText: t("settings.account.deleteAccount"),
      cancelText: t("common.cancel"),
      variant: "danger",
      onConfirm: async () => {
        try {
          setSaving(true);
          await deleteUserAccount();
          analytics.track("account_deleted", {});
          toast.success(t("settings.account.deleteSuccess"));
          signOut();
          navigate(ROUTES.HOME);
        } catch (error) {
          console.error("Error deleting account:", error);
          analytics.track("account_deletion_failed", {
            error: error instanceof Error ? error.message : String(error),
          });
          toast.error(
            error instanceof Error
              ? error.message
              : t("settings.account.deleteError"),
          );
        } finally {
          setSaving(false);
        }
      },
    });
  };

  const tabs: { id: SettingsTab; label: string; icon: React.ElementType }[] = [
    { id: "profile", label: t("settings.tabs.profile"), icon: UserIcon },
    {
      id: "security",
      label: t("settings.tabs.security"),
      icon: ShieldCheckIcon,
    },
    {
      id: "notifications",
      label: t("settings.tabs.notifications"),
      icon: BellIcon,
    },
    {
      id: "preferences",
      label: t("settings.tabs.preferences"),
      icon: GlobeAltIcon,
    },
    { id: "privacy", label: t("settings.tabs.privacy"), icon: EyeIcon },
    {
      id: "subscription",
      label: t("settings.tabs.subscription"),
      icon: CreditCardIcon,
    },
    { id: "usage", label: t("settings.tabs.usage"), icon: ChartBarIcon },
    { id: "account", label: t("settings.tabs.account"), icon: TrashIcon },
  ];

  // Get current plan from subscription data or usePlanLimits (for free users)
  const currentPlanType =
    subscriptionData?.plan_type || subscription?.planType || null;
  const isFreePlan = currentPlanType?.endsWith("_free") || !currentPlanType;

  return (
    <div className="min-h-screen overflow-x-hidden">
      <div className="mx-auto max-w-full min-w-0">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-tiber-900">
            {t("settings.title")}
          </h1>
          <p className="mt-2 text-sm font-medium text-tiber-800">
            {t("settings.subtitle")}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Sidebar - Tabs */}
          <div className="lg:col-span-1">
            <div className="bg-white border-2 border-tiber-900 rounded-lg shadow-sm p-4 space-y-2">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-all duration-200 ${
                      activeTab === tab.id
                        ? "border-2 border-tiber-900 text-tiber-900 shadow-md"
                        : "text-tiber-900 hover:bg-romance-50"
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="text-base font-medium text-tiber-900">
                      {tab.label}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className="bg-white border-2 border-tiber-900 rounded-lg shadow-sm p-6"
            >
              {/* Profile Tab */}
              {activeTab === "profile" && (
                <div className="space-y-6">
                  <div>
                    <h3 className="text-2xl font-bold text-tiber-900 mb-2">
                      {t("settings.profile.title")}
                    </h3>
                    <p className="text-sm font-medium text-tiber-800">
                      {t("settings.profile.description")}
                    </p>
                  </div>

                  <div className="space-y-4">
                    <Input
                      label={t("settings.profile.fullName")}
                      value={profileData.full_name}
                      onChange={(e) =>
                        setProfileData({
                          ...profileData,
                          full_name: e.target.value,
                        })
                      }
                      placeholder={t("settings.profile.fullNamePlaceholder")}
                    />

                    <Input
                      label={t("settings.profile.email")}
                      value={profileData.email}
                      disabled
                    />
                    <p className="text-sm font-medium text-tiber-800">
                      {t("settings.profile.emailNote")}
                    </p>
                  </div>

                  <div className="flex justify-end">
                    <Button
                      onClick={handleSaveProfile}
                      isLoading={saving}
                      variant="primary"
                    >
                      {t("settings.save")}
                    </Button>
                  </div>
                </div>
              )}

              {/* Security Tab */}
              {activeTab === "security" && (
                <div className="space-y-6">
                  <div>
                    <h3 className="text-2xl font-bold text-tiber-900 mb-2">
                      {t("settings.security.title")}
                    </h3>
                    <p className="text-sm font-medium text-tiber-800">
                      {t("settings.security.description")}
                    </p>
                  </div>

                  <div className="space-y-4">
                    <div className="p-4 bg-romance-50 rounded-lg border border-tiber-200">
                      <p className="text-sm text-tiber-800 mb-4">
                        {t("settings.security.passwordNote")}
                      </p>
                      <p className="text-sm text-tiber-700 mb-4">
                        {t(
                          "settings.security.resetFlowNote",
                          "Haz clic en el botón para recibir un enlace seguro en",
                        )}{" "}
                        <strong className="text-juridico">
                          {profileData.email}
                        </strong>{" "}
                        {t(
                          "settings.security.resetFlowNoteEnd",
                          "y poder definir una nueva contraseña.",
                        )}
                      </p>
                      <Button
                        onClick={handleChangePassword}
                        isLoading={saving}
                        variant="primary"
                      >
                        {t(
                          "settings.security.sendResetLink",
                          "Enviar enlace para restablecer contraseña",
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* Notifications Tab */}
              {activeTab === "notifications" && (
                <div className="space-y-6">
                  <div>
                    <h3 className="text-2xl font-bold text-tiber-900 mb-2">
                      {t("settings.notifications.title")}
                    </h3>
                    <p className="text-sm font-medium text-tiber-800">
                      {t("settings.notifications.description")}
                    </p>
                  </div>

                  <div className="space-y-4">
                    {Object.entries(notifications).map(([key, value]) => (
                      <div
                        key={key}
                        className="flex items-center justify-between p-4 border border-tiber-200 rounded-lg"
                      >
                        <div>
                          <h4 className="text-base font-medium text-tiber-900">
                            {t(`settings.notifications.${key}`)}
                          </h4>
                          <p className="text-sm text-tiber-800">
                            {t(`settings.notifications.${key}Description`)}
                          </p>
                        </div>
                        <button
                          onClick={() =>
                            setNotifications({
                              ...notifications,
                              [key]: !value,
                            })
                          }
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                            value ? "bg-tiber-900" : "bg-tiber-300"
                          }`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              value ? "translate-x-6" : "translate-x-1"
                            }`}
                          />
                        </button>
                      </div>
                    ))}
                  </div>

                  <div className="flex justify-end">
                    <Button
                      onClick={handleSaveNotifications}
                      variant="primary"
                      isLoading={saving}
                    >
                      {t("settings.save")}
                    </Button>
                  </div>
                </div>
              )}

              {/* Preferences Tab */}
              {activeTab === "preferences" && (
                <div className="space-y-6">
                  <div>
                    <h3 className="text-2xl font-bold text-tiber-900 mb-2">
                      {t("settings.preferences.title")}
                    </h3>
                    <p className="text-sm font-medium text-tiber-800">
                      {t("settings.preferences.description")}
                    </p>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-tiber-900 mb-2">
                        {t("settings.preferences.language")}
                      </label>
                      <div className="flex items-center space-x-4">
                        <LanguageSwitcher />
                        <span className="text-sm font-light text-titan">
                          {preferences.language === "es"
                            ? "Español"
                            : "English"}
                        </span>
                      </div>
                    </div>

                    <div>
                      <Input
                        label={t("settings.preferences.timezone")}
                        value={preferences.timezone}
                        disabled
                      />
                      <p className="text-sm font-light text-titan mt-1">
                        {t("settings.preferences.timezoneNote")}
                      </p>
                    </div>
                  </div>

                  <div className="flex justify-end">
                    <Button
                      onClick={handleSavePreferences}
                      variant="primary"
                      isLoading={saving}
                    >
                      {t("settings.save")}
                    </Button>
                  </div>
                </div>
              )}

              {/* Privacy Tab */}
              {activeTab === "privacy" && (
                <div className="space-y-6">
                  <div>
                    <h3 className="text-2xl font-bold text-juridico mb-2">
                      {t("settings.privacy.title")}
                    </h3>
                    <p className="text-titan">
                      {t("settings.privacy.description")}
                    </p>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-juridico mb-2">
                        {t("settings.privacy.profileVisibility")}
                      </label>
                      <Select
                        value={privacy.profileVisibility}
                        onChange={(e) =>
                          setPrivacy({
                            ...privacy,
                            profileVisibility: e.target.value,
                          })
                        }
                        options={(() => {
                          const baseOptions = [
                            {
                              label: t("settings.privacy.visibilityPublic"),
                              value: "public",
                            },
                            {
                              label: t("settings.privacy.visibilityPrivate"),
                              value: "private",
                            },
                          ];

                          baseOptions.push({
                            label: t("settings.privacy.visibilityLawyers"),
                            value: "lawyers",
                          });

                          return baseOptions;
                        })()}
                      />
                    </div>

                    <div className="space-y-3">
                      {[
                        {
                          key: "showEmail",
                          label: t("settings.privacy.showEmail"),
                        },
                        {
                          key: "showPhone",
                          label: t("settings.privacy.showPhone"),
                        },
                        {
                          key: "allowSearch",
                          label: t("settings.privacy.allowSearch"),
                        },
                      ].map(({ key, label }) => (
                        <div
                          key={key}
                          className="flex items-center justify-between p-4 border border-tiber-200 rounded-lg"
                        >
                          <span className="font-medium text-tiber-900">
                            {label}
                          </span>
                          <button
                            onClick={() =>
                              setPrivacy({
                                ...privacy,
                                [key]: !privacy[key as keyof typeof privacy],
                              })
                            }
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                              privacy[key as keyof typeof privacy]
                                ? "bg-tiber-900"
                                : "bg-tiber-300"
                            }`}
                          >
                            <span
                              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                privacy[key as keyof typeof privacy]
                                  ? "translate-x-6"
                                  : "translate-x-1"
                              }`}
                            />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="flex justify-end">
                    <Button
                      onClick={handleSavePrivacy}
                      variant="primary"
                      isLoading={saving}
                    >
                      {t("settings.save")}
                    </Button>
                  </div>
                </div>
              )}

              {/* Subscription Tab */}
              {activeTab === "subscription" && (
                <div className="space-y-6">
                  <div>
                    <h3 className="text-2xl font-bold text-tiber-900 mb-2">
                      {t("settings.subscription.title")}
                    </h3>
                    <p className="text-sm font-medium text-tiber-800">
                      {t("settings.subscription.description")}
                    </p>
                  </div>

                  {subscriptionLoading ? (
                    <div className="flex justify-center py-12">
                      <div className="animate-spin h-10 w-10 border-2 border-tiber-500 border-t-transparent rounded-full" />
                    </div>
                  ) : subscriptionData ? (
                    <>
                      <div className="space-y-6">
                        {/* Current Subscription */}
                        <div className="bg-linear-to-br from-tiber-700 to-tiber-900 rounded-xl p-6 text-white">
                          <div className="flex items-center justify-between mb-4">
                            <div>
                              <h4 className="text-xl font-bold mb-2">
                                {t("settings.subscription.currentPlan")}
                              </h4>
                              <p className="text-tiber-100">
                                {formatPlanLabel(
                                  subscriptionData.plan_type,
                                  subscriptionData.plan_tier,
                                ).toUpperCase()}{" "}
                                - {subscriptionData.status.toUpperCase()}
                              </p>
                            </div>
                            <div className="px-4 py-2 bg-white/20 backdrop-blur-sm rounded-lg font-bold">
                              {subscriptionData.status === "trialing" ||
                              subscriptionData.plan_type === "lawyer_trial"
                                ? t("settings.subscription.trialBadge", "Trial")
                                : formatPlanLabel(
                                    subscriptionData.plan_type,
                                  ).toUpperCase()}
                            </div>
                          </div>

                          <div className="grid grid-cols-2 gap-4 mt-4">
                            {subscriptionData.status !== "trialing" &&
                              subscriptionData.plan_type !== "lawyer_trial" &&
                              subscriptionData.plan_type !== "lawyer_free" &&
                              subscriptionData.plan_type !== "client_free" &&
                              subscriptionData.current_period_start &&
                              subscriptionData.current_period_end && (
                                <div>
                                  <p className="text-sm text-tiber-100 mb-1">
                                    {t("settings.subscription.billingPeriod")}
                                  </p>
                                  <p className="font-semibold">
                                    {new Date(
                                      subscriptionData.current_period_start,
                                    ).toLocaleDateString()}{" "}
                                    -{" "}
                                    {new Date(
                                      subscriptionData.current_period_end,
                                    ).toLocaleDateString()}
                                  </p>
                                </div>
                              )}
                            <div>
                              <p className="text-sm text-tiber-100 mb-1">
                                {t("settings.subscription.status")}
                              </p>
                              <p className="font-semibold capitalize">
                                {subscriptionData.status}
                              </p>
                            </div>
                          </div>

                          {subscriptionData.status === "trialing" &&
                            (subscriptionData.trial_start ||
                              subscriptionData.trial_end) && (
                              <div className="mt-4 p-3 bg-white/10 rounded-lg border border-white/20">
                                <p className="text-sm text-tiber-100 mb-1">
                                  {t("settings.subscription.trialPeriod")}
                                </p>
                                <p className="font-semibold">
                                  {subscriptionData.trial_start &&
                                  subscriptionData.trial_end
                                    ? `${new Date(
                                        subscriptionData.trial_start,
                                      ).toLocaleDateString()} - ${new Date(
                                        subscriptionData.trial_end,
                                      ).toLocaleDateString()}`
                                    : subscriptionData.trial_end
                                      ? `${t("settings.subscription.trialEndsOn")} ${new Date(subscriptionData.trial_end).toLocaleDateString()}`
                                      : subscriptionData.trial_start
                                        ? new Date(
                                            subscriptionData.trial_start,
                                          ).toLocaleDateString()
                                        : ""}
                                </p>
                              </div>
                            )}

                          {subscriptionData.cancel_at_period_end && (
                            <div className="mt-4 p-3 bg-amber-500/20 rounded-lg border border-amber-300/30">
                              <p className="text-sm">
                                {t(
                                  "settings.subscription.cancelingAtPeriodEnd",
                                )}
                              </p>
                            </div>
                          )}
                        </div>

                        {/* Actions */}
                        <div className="flex flex-wrap gap-4">
                          <Button
                            variant="secondary"
                            onClick={async () => {
                              try {
                                setOpeningPortal(true);
                                const url = await createPortalSession();
                                window.location.href = url;
                              } catch (error) {
                                console.error("Portal error:", error);
                                toast.error(
                                  t("settings.subscription.portalError"),
                                );
                              } finally {
                                setOpeningPortal(false);
                              }
                            }}
                            isLoading={openingPortal}
                          >
                            {t("settings.subscription.manageBilling")}
                          </Button>

                          {!subscriptionData.cancel_at_period_end ? (
                            <Button
                              variant="secondary"
                              onClick={async () => {
                                await confirm({
                                  title: t("settings.subscription.cancelTitle"),
                                  message: t(
                                    "settings.subscription.cancelMessage",
                                  ),
                                  confirmText: t(
                                    "settings.subscription.cancel",
                                  ),
                                  cancelText: t("common.cancel"),
                                  variant: "danger",
                                  onConfirm: async () => {
                                    try {
                                      setCanceling(true);
                                      await cancelSubscription(
                                        subscriptionData.id,
                                        true,
                                      );
                                      analytics.subscriptionCancelled(
                                        subscriptionData?.plan_type ??
                                          "unknown",
                                      );
                                      await refreshSubscriptionData();
                                      await refresh();
                                      toast.success(
                                        t("settings.subscription.canceled"),
                                      );
                                    } catch (error) {
                                      console.error(
                                        "Error canceling subscription:",
                                        error,
                                      );
                                      toast.error(
                                        t("settings.subscription.cancelError"),
                                      );
                                    } finally {
                                      setCanceling(false);
                                    }
                                  },
                                });
                              }}
                              isLoading={canceling}
                              className="text-red-600 hover:text-red-700"
                            >
                              {t(
                                "settings.subscription.cancelSubscription",
                                "Cancel Subscription",
                              )}
                            </Button>
                          ) : (
                            <Button
                              variant="primary"
                              onClick={async () => {
                                try {
                                  setCanceling(true);
                                  await reactivateSubscription(
                                    subscriptionData.id,
                                  );
                                  await refreshSubscriptionData();
                                  await refresh();
                                  toast.success(
                                    t("settings.subscription.reactivated"),
                                  );
                                } catch (error) {
                                  console.error(
                                    "Error reactivating subscription:",
                                    error,
                                  );
                                  toast.error(
                                    t("settings.subscription.reactivateError"),
                                  );
                                } finally {
                                  setCanceling(false);
                                }
                              }}
                              isLoading={canceling}
                            >
                              {t("settings.subscription.reactivate")}
                            </Button>
                          )}
                          <Button
                            variant="primary"
                            onClick={() => setPricingModalOpen(true)}
                          >
                            {t("settings.subscription.changePlan")}
                          </Button>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="bg-linear-to-br from-tiber-700 to-tiber-900 rounded-xl p-6 text-white">
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h4 className="text-xl font-bold mb-2">
                            {t("settings.subscription.currentPlan")}
                          </h4>
                          <p className="text-tiber-100 capitalize">
                            {profile?.user_type === "lawyer"
                              ? t(
                                  "settings.subscription.freePlanLawyer",
                                  "Plan Abogado Gratuito",
                                )
                              : profile?.user_type === "client"
                                ? t(
                                    "settings.subscription.freePlanClient",
                                    "Plan Cliente Gratuito",
                                  )
                                : t(
                                    "settings.subscription.freePlan",
                                    "Plan Gratuito",
                                  )}
                          </p>
                        </div>
                        <div className="px-4 py-2 bg-white/20 backdrop-blur-sm rounded-lg font-bold">
                          FREE
                        </div>
                      </div>
                      <p className="text-sm text-tiber-100 mb-4">
                        {t("settings.subscription.noSubscriptionDesc")}
                      </p>
                      {profile?.user_type === "lawyer" &&
                        profile?.onboarding_completed && (
                          <p className="text-sm text-tiber-200 mb-4">
                            {t(
                              "settings.subscription.trialActivatingHint",
                              "Si acabas de completar tu registro, tu prueba de 3 días puede estar activándose. Actualiza para verificar.",
                            )}
                          </p>
                        )}
                      <div className="flex gap-3">
                        {profile?.user_type === "lawyer" &&
                          profile?.onboarding_completed && (
                            <Button
                              variant="secondary"
                              onClick={() => refreshSubscriptionData()}
                              className="bg-white/10 hover:bg-white/20 text-white border-white/30"
                            >
                              {t("common.refresh")}
                            </Button>
                          )}
                        <Button
                          variant="primary"
                          onClick={() => setPricingModalOpen(true)}
                          leftIcon={<ArrowUpIcon className="w-5 h-5" />}
                        >
                          {t("settings.subscription.viewPlans")}
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Credits Section - Always visible when loaded (free and subscribed) */}
                  {!subscriptionLoading && (
                    <div className="bg-white border-2 border-tiber-200 rounded-xl p-6">
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="text-lg font-bold text-tiber-900 flex items-center gap-2">
                          <SparklesIcon className="w-5 h-5 text-tiber-500" />
                          {t("settings.subscription.credits")}
                        </h4>
                        {!isTrialing ? (
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => setBuyCreditsModalOpen(true)}
                            leftIcon={<SparklesIcon className="w-4 h-4" />}
                          >
                            {t("settings.subscription.buyCredits")}
                          </Button>
                        ) : (
                          <span
                            className="text-sm text-tiber-500"
                            title={t(
                              "settings.subscription.trialingNoBuyCredits",
                            )}
                          >
                            {t("settings.subscription.trialingNoBuyCredits")}
                          </span>
                        )}
                      </div>
                      <div className="mb-4">
                        <p className="text-3xl font-bold text-tiber-900">
                          {creditsData?.balance ?? 0}
                        </p>
                        <p className="text-sm text-tiber-600">
                          {t("settings.subscription.creditsAvailable")}
                        </p>
                      </div>
                      {creditsData?.transactions &&
                        creditsData.transactions.length > 0 && (
                          <div>
                            <h5 className="text-sm font-semibold text-tiber-900 mb-2">
                              {t("settings.subscription.recentTransactions")}
                            </h5>
                            <div className="space-y-2 max-h-48 overflow-y-auto">
                              {creditsData.transactions
                                .slice(0, 5)
                                .map((transaction) => (
                                  <div
                                    key={transaction.id}
                                    className="flex items-center justify-between p-2 bg-romance-50 rounded-lg text-sm"
                                  >
                                    <div>
                                      <p className="font-medium text-tiber-900">
                                        {transaction.transaction_type ===
                                        "purchase"
                                          ? t("settings.subscription.purchased")
                                          : transaction.transaction_type ===
                                              "usage"
                                            ? t("settings.subscription.used")
                                            : transaction.transaction_type}
                                      </p>
                                      <p className="text-xs text-tiber-600">
                                        {new Date(
                                          transaction.created_at,
                                        ).toLocaleDateString()}
                                      </p>
                                    </div>
                                    <span
                                      className={`font-semibold ${
                                        transaction.amount > 0
                                          ? "text-atlantis-600"
                                          : "text-red-600"
                                      }`}
                                    >
                                      {transaction.amount > 0 ? "+" : ""}
                                      {transaction.amount}{" "}
                                      {t("settings.subscription.credits")}
                                    </span>
                                  </div>
                                ))}
                            </div>
                          </div>
                        )}
                    </div>
                  )}
                </div>
              )}

              {/* Usage Tab */}
              {activeTab === "usage" && (
                <div className="space-y-6">
                  <div>
                    <h3 className="text-2xl font-bold text-tiber-900">
                      {t("settings.usage.title")}
                    </h3>
                    <p className="text-sm font-light text-tiber-800">
                      {t("settings.usage.description")}
                    </p>
                  </div>

                  {limitsLoading || subscriptionLoading || !limits ? (
                    <div className="flex items-center justify-center py-12">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-tiber-900"></div>
                    </div>
                  ) : (
                    <>
                      {/* Current Plan Overview */}
                      <div className="bg-white border-2 border-tiber-200 rounded-xl p-6 shadow-sm">
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center gap-4">
                            <div className="p-3 bg-tiber-100 rounded-xl">
                              <SparklesIcon className="w-8 h-8 text-tiber-700" />
                            </div>
                            <div>
                              <h4 className="text-xl font-bold text-tiber-900">
                                {t("settings.usage.currentPlan")}
                              </h4>
                              <p className="text-sm text-tiber-600">
                                {currentPlanType
                                  ? currentPlanType
                                      .replace(/_/g, " ")
                                      .replace(/\b\w/g, (l: string) =>
                                        l.toUpperCase(),
                                      )
                                  : t("settings.usage.freePlanDesc")}
                              </p>
                            </div>
                          </div>
                          <div className="px-4 py-2 bg-tiber-900 text-white rounded-lg font-bold text-sm">
                            {subscriptionData?.status === "trialing"
                              ? t("settings.subscription.trialBadge", "Trial")
                              : !isFreePlan
                                ? currentPlanType
                                    ?.split("_")
                                    .slice(1)
                                    .join(" ")
                                    .toUpperCase() || "PROFESSIONAL"
                                : "FREE"}
                          </div>
                        </div>

                        {/* Billing Period - only for paid plans (not trialing, not free) */}
                        {subscriptionData?.status !== "trialing" &&
                          subscriptionData?.plan_type !== "lawyer_trial" &&
                          subscriptionData?.plan_type !== "lawyer_free" &&
                          subscriptionData?.plan_type !== "client_free" &&
                          subscriptionData?.current_period_start &&
                          subscriptionData?.current_period_end && (
                            <div className="flex items-center gap-6 pt-4 border-t border-tiber-100">
                              <div>
                                <p className="text-xs text-tiber-500 mb-1">
                                  {t("settings.subscription.billingPeriod")}
                                </p>
                                <p className="text-sm font-medium text-tiber-900">
                                  {new Date(
                                    subscriptionData.current_period_start,
                                  ).toLocaleDateString()}{" "}
                                  -{" "}
                                  {new Date(
                                    subscriptionData.current_period_end,
                                  ).toLocaleDateString()}
                                </p>
                              </div>
                              <div>
                                <p className="text-xs text-tiber-500 mb-1">
                                  {t("settings.subscription.status")}
                                </p>
                                <span
                                  className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                    subscriptionData.status === "active"
                                      ? "bg-atlantis-100 text-atlantis-700"
                                      : "bg-amber-100 text-amber-700"
                                  }`}
                                >
                                  {subscriptionData.status
                                    ?.charAt(0)
                                    .toUpperCase() +
                                    subscriptionData.status?.slice(1)}
                                </span>
                              </div>
                            </div>
                          )}

                        {/* Trial Period - when status is trialing */}
                        {subscriptionData?.status === "trialing" &&
                          (subscriptionData.trial_start ||
                            subscriptionData.trial_end) && (
                            <div className="flex items-center gap-6 pt-4 border-t border-tiber-100">
                              <div>
                                <p className="text-xs text-tiber-500 mb-1">
                                  {t("settings.subscription.trialPeriod")}
                                </p>
                                <p className="text-sm font-medium text-tiber-900">
                                  {subscriptionData.trial_start &&
                                  subscriptionData.trial_end
                                    ? `${new Date(subscriptionData.trial_start).toLocaleDateString()} - ${new Date(subscriptionData.trial_end).toLocaleDateString()}`
                                    : subscriptionData.trial_end
                                      ? `${t("settings.subscription.trialEndsOn")} ${new Date(subscriptionData.trial_end).toLocaleDateString()}`
                                      : subscriptionData.trial_start
                                        ? new Date(
                                            subscriptionData.trial_start,
                                          ).toLocaleDateString()
                                        : ""}
                                </p>
                              </div>
                            </div>
                          )}

                        {isFreePlan && (
                          <div className="mt-4 pt-4 border-t border-tiber-100">
                            <Button
                              onClick={() => navigate(ROUTES.PRICING)}
                              variant="primary"
                              leftIcon={<ArrowUpIcon className="w-5 h-5" />}
                            >
                              {t("settings.usage.upgradeToPro")}
                            </Button>
                          </div>
                        )}
                      </div>

                      {/* Usage This Month */}
                      <div className="bg-white border-2 border-tiber-200 rounded-xl p-6 shadow-sm">
                        <h4 className="text-lg font-bold text-tiber-900 flex items-center gap-2 mb-4">
                          <ChartBarIcon className="w-5 h-5 text-tiber-600" />
                          {t("settings.subscription.usageThisMonth")}
                        </h4>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {/* Documents Generated */}
                          <div className="p-4 bg-tiber-50 rounded-lg">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <DocumentTextIcon className="w-5 h-5 text-tiber-600" />
                                <span className="text-sm font-medium text-tiber-800">
                                  {t("settings.usage.documents")}
                                </span>
                              </div>
                              <span className="text-sm font-bold text-tiber-900">
                                {usageSummary.document_generation || 0}
                                {limits.documents_per_month !== null
                                  ? ` / ${limits.documents_per_month}`
                                  : ""}
                              </span>
                            </div>
                            {limits.documents_per_month !== null ? (
                              <div className="w-full bg-tiber-200 rounded-full h-2">
                                <div
                                  className="bg-tiber-600 h-2 rounded-full transition-all duration-300"
                                  style={{
                                    width: `${Math.min(
                                      ((usageSummary.document_generation || 0) /
                                        limits.documents_per_month) *
                                        100,
                                      100,
                                    )}%`,
                                  }}
                                />
                              </div>
                            ) : (
                              <span className="text-xs text-atlantis-600 font-medium">
                                ✓ {t("settings.usage.unlimited")}
                              </span>
                            )}
                          </div>

                          {/* Consultations */}
                          <div className="p-4 bg-tiber-50 rounded-lg">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <ChatBubbleLeftRightIcon className="w-5 h-5 text-tiber-600" />
                                <span className="text-sm font-medium text-tiber-800">
                                  {t("settings.usage.consultations")}
                                </span>
                              </div>
                              <span className="text-sm font-bold text-tiber-900">
                                {usageSummary.consultation || 0}
                                {limits.consultations_per_month !== null
                                  ? ` / ${limits.consultations_per_month}`
                                  : ""}
                              </span>
                            </div>
                            {limits.consultations_per_month !== null ? (
                              <div className="w-full bg-tiber-200 rounded-full h-2">
                                <div
                                  className="bg-tiber-600 h-2 rounded-full transition-all duration-300"
                                  style={{
                                    width: `${Math.min(
                                      ((usageSummary.consultation || 0) /
                                        limits.consultations_per_month) *
                                        100,
                                      100,
                                    )}%`,
                                  }}
                                />
                              </div>
                            ) : (
                              <span className="text-xs text-atlantis-600 font-medium">
                                ✓ {t("settings.usage.unlimited")}
                              </span>
                            )}
                          </div>

                          {/* Cases */}
                          <div className="p-4 bg-tiber-50 rounded-lg">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <BriefcaseIcon className="w-5 h-5 text-tiber-600" />
                                <span className="text-sm font-medium text-tiber-800">
                                  {t("settings.usage.cases")}
                                </span>
                              </div>
                              <span className="text-sm font-bold text-tiber-900">
                                {usageSummary.case || 0}
                                {limits.cases_per_month !== null
                                  ? ` / ${limits.cases_per_month}`
                                  : ""}
                              </span>
                            </div>
                            {limits.cases_per_month !== null ? (
                              <div className="w-full bg-tiber-200 rounded-full h-2">
                                <div
                                  className="bg-tiber-600 h-2 rounded-full transition-all duration-300"
                                  style={{
                                    width: `${Math.min(
                                      ((usageSummary.case || 0) /
                                        limits.cases_per_month) *
                                        100,
                                      100,
                                    )}%`,
                                  }}
                                />
                              </div>
                            ) : (
                              <span className="text-xs text-atlantis-600 font-medium">
                                ✓ {t("settings.usage.unlimited")}
                              </span>
                            )}
                          </div>

                          {/* Document Reviews */}
                          <div className="p-4 bg-tiber-50 rounded-lg">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <MagnifyingGlassIcon className="w-5 h-5 text-tiber-600" />
                                <span className="text-sm font-medium text-tiber-800">
                                  {t("settings.usage.documentReviews")}
                                </span>
                              </div>
                              <span className="text-sm font-bold text-tiber-900">
                                {usageSummary.document_review || 0}
                                {limits.document_reviews_per_month !== null
                                  ? ` / ${limits.document_reviews_per_month}`
                                  : ""}
                              </span>
                            </div>
                            {limits.document_reviews_per_month !== null ? (
                              <div className="w-full bg-tiber-200 rounded-full h-2">
                                <div
                                  className="bg-tiber-600 h-2 rounded-full transition-all duration-300"
                                  style={{
                                    width: `${Math.min(
                                      ((usageSummary.document_review || 0) /
                                        limits.document_reviews_per_month) *
                                        100,
                                      100,
                                    )}%`,
                                  }}
                                />
                              </div>
                            ) : (
                              <span className="text-xs text-atlantis-600 font-medium">
                                ✓ {t("settings.usage.unlimited")}
                              </span>
                            )}
                          </div>

                          {/* Case Summaries */}
                          <div className="p-4 bg-tiber-50 rounded-lg">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <DocumentMagnifyingGlassIcon className="w-5 h-5 text-tiber-600" />
                                <span className="text-sm font-medium text-tiber-800">
                                  {t("settings.usage.caseSummaries")}
                                </span>
                              </div>
                              <span className="text-sm font-bold text-tiber-900">
                                {usageSummary.case_summary || 0}
                                {limits.case_summaries_per_month !== null
                                  ? ` / ${limits.case_summaries_per_month}`
                                  : ""}
                              </span>
                            </div>
                            {limits.case_summaries_per_month !== null ? (
                              <div className="w-full bg-tiber-200 rounded-full h-2">
                                <div
                                  className="bg-tiber-600 h-2 rounded-full transition-all duration-300"
                                  style={{
                                    width: `${Math.min(
                                      ((usageSummary.case_summary || 0) /
                                        limits.case_summaries_per_month) *
                                        100,
                                      100,
                                    )}%`,
                                  }}
                                />
                              </div>
                            ) : (
                              <span className="text-xs text-atlantis-600 font-medium">
                                ✓ {t("settings.usage.unlimited")}
                              </span>
                            )}
                          </div>

                          {/* AI Legal Chat */}
                          {limits.legal_ai_queries_per_month !== undefined && (
                            <div className="p-4 bg-tiber-50 rounded-lg">
                              <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-2">
                                  <SparklesIcon className="w-5 h-5 text-tiber-600" />
                                  <span className="text-sm font-medium text-tiber-800">
                                    {t("settings.usage.aiQueries")}
                                  </span>
                                </div>
                                <span className="text-sm font-bold text-tiber-900">
                                  {usageSummary.legal_ai_chat || 0}
                                  {limits.legal_ai_queries_per_month !== null
                                    ? ` / ${limits.legal_ai_queries_per_month}`
                                    : ""}
                                </span>
                              </div>
                              {limits.legal_ai_queries_per_month !== null ? (
                                <div className="w-full bg-tiber-200 rounded-full h-2">
                                  <div
                                    className="bg-tiber-600 h-2 rounded-full transition-all duration-300"
                                    style={{
                                      width: `${Math.min(
                                        ((usageSummary.legal_ai_chat || 0) /
                                          limits.legal_ai_queries_per_month) *
                                          100,
                                        100,
                                      )}%`,
                                    }}
                                  />
                                </div>
                              ) : (
                                <span className="text-xs text-atlantis-600 font-medium">
                                  ✓ {t("settings.usage.unlimited")}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Credits Section - Always visible (free and subscribed) */}
                      <div className="bg-white border-2 border-tiber-200 rounded-xl p-6 shadow-sm">
                        <div className="flex items-center justify-between mb-4">
                          <h4 className="text-lg font-bold text-tiber-900 flex items-center gap-2">
                            <SparklesIcon className="w-5 h-5 text-tiber-500" />
                            {t("settings.subscription.credits")}
                          </h4>
                          {!isTrialing ? (
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={() => setBuyCreditsModalOpen(true)}
                              leftIcon={<SparklesIcon className="w-4 h-4" />}
                            >
                              {t("settings.subscription.buyCredits")}
                            </Button>
                          ) : (
                            <span className="text-sm text-tiber-500">
                              {t("settings.subscription.trialingNoBuyCredits")}
                            </span>
                          )}
                        </div>
                        <div className="mb-4">
                          <p className="text-3xl font-bold text-tiber-900">
                            {creditsData?.balance ?? 0}
                          </p>
                          <p className="text-sm text-tiber-600">
                            {t("settings.subscription.creditsAvailable")}
                          </p>
                        </div>
                        {(creditsData?.balance ?? 0) <= 5 &&
                          (creditsData?.balance ?? 0) > 0 && (
                            <p className="text-xs text-amber-600 mb-4">
                              {t("settings.usage.lowCredits")}
                            </p>
                          )}
                        {creditsData?.transactions &&
                          creditsData.transactions.length > 0 && (
                            <div>
                              <h5 className="text-sm font-semibold text-tiber-900 mb-2">
                                {t("settings.subscription.recentTransactions")}
                              </h5>
                              <div className="space-y-2 max-h-48 overflow-y-auto">
                                {creditsData.transactions
                                  .slice(0, 5)
                                  .map((transaction) => (
                                    <div
                                      key={transaction.id}
                                      className="flex items-center justify-between p-2 bg-romance-50 rounded-lg text-sm"
                                    >
                                      <div>
                                        <p className="font-medium text-tiber-900">
                                          {transaction.transaction_type ===
                                          "purchase"
                                            ? t(
                                                "settings.subscription.purchased",
                                              )
                                            : transaction.transaction_type ===
                                                "usage"
                                              ? t("settings.subscription.used")
                                              : transaction.transaction_type}
                                        </p>
                                        <p className="text-xs text-tiber-600">
                                          {new Date(
                                            transaction.created_at,
                                          ).toLocaleDateString()}
                                        </p>
                                      </div>
                                      <span
                                        className={`font-semibold ${
                                          transaction.amount > 0
                                            ? "text-atlantis-600"
                                            : "text-red-600"
                                        }`}
                                      >
                                        {transaction.amount > 0 ? "+" : ""}
                                        {transaction.amount}{" "}
                                        {t("settings.subscription.credits")}
                                      </span>
                                    </div>
                                  ))}
                              </div>
                            </div>
                          )}
                      </div>

                      {/* Plan Features */}
                      <div className="bg-white border-2 border-tiber-200 rounded-xl p-6 shadow-sm">
                        <h4 className="text-lg font-bold text-tiber-900 flex items-center gap-2 mb-4">
                          <CheckCircleIcon className="w-5 h-5 text-tiber-600" />
                          {t("settings.usage.planFeatures")}
                        </h4>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                          <div
                            className={`flex items-center gap-2 p-3 rounded-lg ${limits.has_ai_document_review ? "bg-atlantis-50 text-atlantis-700" : "bg-tiber-100 text-titan"}`}
                          >
                            {limits.has_ai_document_review ? (
                              <CheckCircleIcon className="w-5 h-5" />
                            ) : (
                              <XCircleIcon className="w-5 h-5" />
                            )}
                            <span className="text-sm font-medium">
                              {t("settings.usage.aiDocReview")}
                            </span>
                          </div>

                          <div
                            className={`flex items-center gap-2 p-3 rounded-lg ${limits.has_case_summary ? "bg-atlantis-50 text-atlantis-700" : "bg-tiber-100 text-titan"}`}
                          >
                            {limits.has_case_summary ? (
                              <CheckCircleIcon className="w-5 h-5" />
                            ) : (
                              <XCircleIcon className="w-5 h-5" />
                            )}
                            <span className="text-sm font-medium">
                              {t("settings.usage.caseSummaryFeature")}
                            </span>
                          </div>

                          <div
                            className={`flex items-center gap-2 p-3 rounded-lg ${limits.has_automatic_contracts ? "bg-atlantis-50 text-atlantis-700" : "bg-tiber-100 text-titan"}`}
                          >
                            {limits.has_automatic_contracts ? (
                              <CheckCircleIcon className="w-5 h-5" />
                            ) : (
                              <XCircleIcon className="w-5 h-5" />
                            )}
                            <span className="text-sm font-medium">
                              {t("settings.usage.autoContracts")}
                            </span>
                          </div>

                          <div
                            className={`flex items-center gap-2 p-3 rounded-lg ${limits.has_billing_reports ? "bg-atlantis-50 text-atlantis-700" : "bg-tiber-100 text-titan"}`}
                          >
                            {limits.has_billing_reports ? (
                              <CheckCircleIcon className="w-5 h-5" />
                            ) : (
                              <XCircleIcon className="w-5 h-5" />
                            )}
                            <span className="text-sm font-medium">
                              {t("settings.usage.billingReports")}
                            </span>
                          </div>

                          <div
                            className={`flex items-center gap-2 p-3 rounded-lg ${limits.has_advanced_analytics ? "bg-atlantis-50 text-atlantis-700" : "bg-tiber-100 text-titan"}`}
                          >
                            {limits.has_advanced_analytics ? (
                              <CheckCircleIcon className="w-5 h-5" />
                            ) : (
                              <XCircleIcon className="w-5 h-5" />
                            )}
                            <span className="text-sm font-medium">
                              {t("settings.usage.advancedAnalytics")}
                            </span>
                          </div>

                          <div
                            className={`flex items-center gap-2 p-3 rounded-lg ${limits.has_priority_support ? "bg-atlantis-50 text-atlantis-700" : "bg-tiber-100 text-titan"}`}
                          >
                            {limits.has_priority_support ? (
                              <CheckCircleIcon className="w-5 h-5" />
                            ) : (
                              <XCircleIcon className="w-5 h-5" />
                            )}
                            <span className="text-sm font-medium">
                              {t("settings.usage.prioritySupport")}
                            </span>
                          </div>
                        </div>

                        {/* Storage Info */}
                        <div className="mt-4 pt-4 border-t border-tiber-100">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-tiber-700">
                              {t("settings.usage.storage")}
                            </span>
                            <span className="text-sm font-bold text-tiber-900">
                              {formatMegabytes(limits.storage_mb || 0)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* Account Tab */}
              {activeTab === "account" && (
                <div className="space-y-6">
                  <div>
                    <h3 className="text-2xl font-bold text-juridico mb-2">
                      {t("settings.account.title")}
                    </h3>
                    <p className="text-titan">
                      {t("settings.account.description")}
                    </p>
                  </div>

                  <div className="space-y-4">
                    <div className="p-4 border-2 border-red-200 bg-red-50 rounded-lg">
                      <h4 className="font-bold text-red-800 mb-2">
                        {t("settings.account.dangerZone")}
                      </h4>
                      <p className="text-sm text-red-700 mb-4">
                        {t("settings.account.deleteWarning")}
                      </p>
                      <Button
                        onClick={handleDeleteAccount}
                        isLoading={saving}
                        variant="ghost"
                        className="bg-red-600 text-white hover:bg-red-700"
                        leftIcon={<TrashIcon className="w-5 h-5" />}
                      >
                        {t("settings.account.deleteAccount")}
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </motion.div>
          </div>
        </div>
        {Modal}
      </div>

      {/* Pricing Modal */}
      <PricingModal
        isOpen={pricingModalOpen}
        onClose={() => setPricingModalOpen(false)}
        currentUserType={profile?.user_type === "lawyer" ? "lawyer" : null}
      />

      {/* Buy Credits Modal */}
      <BuyCreditsModal
        isOpen={buyCreditsModalOpen}
        onClose={() => setBuyCreditsModalOpen(false)}
        onSuccess={() => {
          refreshSubscriptionData();
          refresh();
        }}
      />
    </div>
  );
};
